import type { ReactNode } from "react";
import styled from "styled-components";

const PURPLE = "#3a0ca3";
const TEXT = "#303030";

export const BRAND_AGENCY_PRIVACY_NOTICE_META = {
	title: "Brand and Agency Privacy Notice",
	version: "Version 1.1",
	companyName: "CREATORVERSE PTE. LTD.",
	companyLine: "UEN 202629528Z · 69 Circular Road, #02-01, Singapore 049423",
	effectiveDate: "1 October 2026",
	lastUpdated: "1 October 2026",
} as const;

const Document = styled.article`
	box-sizing: border-box;
	width: 100%;
	padding: 32px;
	display: flex;
	flex-direction: column;
	gap: 48px;
	color: ${TEXT};
	font-family: Poppins, sans-serif;
	font-size: 14px;
	line-height: 24px;
	word-break: break-word;

	* {
		box-sizing: border-box;
	}

	p {
		margin: 0;
	}

	strong {
		font-weight: 600;
	}

	@media (max-width: 700px) {
		padding: 20px 16px;
		gap: 32px;
	}
`;

const Header = styled.header`
	padding-bottom: 24px;
`;

const Logo = styled.img`
	display: block;
	width: 240px;
	height: auto;
	max-width: 65%;
`;

const Content = styled.div`
	display: flex;
	flex-direction: column;
	gap: 40px;
`;

const Section = styled.section`
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding-left: 72px;

	@media (max-width: 600px) {
		padding-left: 0;
	}
`;

const SectionTitle = styled.h2`
	margin: 0;
	color: ${PURPLE};
	font-size: 14px;
	font-weight: 600;
	line-height: 24px;
`;

const Group = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const GroupTitle = styled.h3`
	margin: 0;
	font-size: 14px;
	font-weight: 600;
	line-height: 24px;
`;

const Bullets = styled.ul`
	display: flex;
	flex-direction: column;
	gap: 6px;
	margin: 0;
	padding-left: 16px;
	list-style: none;

	li::before {
		content: "• ";
	}
`;

const Table = styled.div`
	overflow: hidden;
	border: 1px solid #cdd4f6;
	border-radius: 8px;
`;

const TableRow = styled.div<{ $header?: boolean }>`
	display: grid;
	grid-template-columns: minmax(0, 480fr) minmax(0, 408fr);
	gap: 24px;
	padding: 12px 16px;
	background: ${({ $header }) => ($header ? "#dde2fa" : "#f3f5ff")};
	color: ${({ $header }) => ($header ? "#011992" : TEXT)};
	font-weight: ${({ $header }) => ($header ? 600 : 400)};

	> :first-child {
		font-weight: 600;
	}
`;

const RETENTION: Array<[string, string]> = [
	["Your name, work contact details and account data", "For the life of the account, and 12 months afterwards"],
	["Records of your acceptance of our terms", "7 years"],
	["Campaign records you are named on", "7 years, in line with the campaign’s financial records"],
	["Device, IP address and usage data", "24 months"],
	["Support messages and correspondence", "3 years"],
	["Accounts that were never activated", "90 days"],
];

const NoticeSection = ({ title, children }: { title: string; children: ReactNode }) => (
	<Section>
		<SectionTitle>{title}</SectionTitle>
		{children}
	</Section>
);

/** Brand and Agency Privacy Notice v1.1, rendered inside `LegalDocumentModal`. */
export const BrandAgencyPrivacyNoticeDocument = () => (
	<Document>
		<Header>
			<Logo src="/Creatorverse_Wordmark_Purple.svg" alt="Creatorverse" />
		</Header>
		<Content>
			<NoticeSection title="Who we are">
				<p>
					<strong>Creatorverse Pte. Ltd.</strong> (UEN 202629528Z) operates the Creatorverse platform. For the
					purposes of applicable data protection law, including Singapore’s Personal Data Protection Act 2012
					(<strong>PDPA</strong>), we are the organisation responsible for the personal data described in this
					Notice.
				</p>
				<p>
					Contact our Data Protection Officer at <strong>privacy@thecreatorverse.co</strong>, or by post at 69
					Circular Road, #02-01, Singapore 049423.
				</p>
				<p>
					<strong>
						This Notice applies to you if you are a brand, advertiser or agency user of the Platform.
					</strong>{" "}
					Creators are covered by our separate, fuller Privacy Policy.
				</p>
			</NoticeSection>

			<NoticeSection title="What we collect">
				<Group>
					<GroupTitle>Information you give us</GroupTitle>
					<Bullets>
						<li>Your name, job title, work email address and work phone number</li>
						<li>Your organisation and the client or brand you act for</li>
						<li>Your account credentials</li>
						<li>Messages, support tickets and feedback you send us</li>
					</Bullets>
				</Group>
				<Group>
					<GroupTitle>Information we collect automatically when you use the Platform</GroupTitle>
					<Bullets>
						<li>IP address, device identifiers, browser type and operating system</li>
						<li>Which pages and features you view, actions you take, and when</li>
						<li>Cookies and similar technologies</li>
					</Bullets>
				</Group>
				<p>
					<strong>We do not collect</strong> your personal financial details, bank details or tax information.
					Those are only ever collected from creators who opt into the payment service, by a different
					company.
				</p>
			</NoticeSection>

			<NoticeSection title="Why we use it">
				<Bullets>
					<li>
						To create and administer your account and give you and your Authorised Users access to the
						Platform
					</li>
					<li>To run campaigns you are involved in, and to provide reporting and analytics</li>
					<li>To communicate with you about the Platform, including service and security messages</li>
					<li>To secure the Platform, prevent fraud and misuse, and enforce our terms</li>
					<li>To improve the Platform and develop new features</li>
					<li>
						To send you marketing about our products and services,{" "}
						<strong>only where you have opted in</strong>
					</li>
					<li>To comply with our legal, regulatory and accounting obligations</li>
				</Bullets>
			</NoticeSection>

			<NoticeSection title="Who we share it with">
				<p>We do not sell your personal data. We share it with:</p>
				<Bullets>
					<li>
						<strong>Service providers</strong> who act on our instructions — hosting, analytics, customer
						support and communications (currently Amazon Web Services, which hosts the Platform and its
						data, and Google, where you sign in with a Google account)
					</li>
					<li>
						<strong>Creator Technology Solutions Pte. Ltd.</strong>, our parent company, where necessary for
						group administration
					</li>
					<li>
						<strong>Campaign counterparties</strong> — relevant campaign details are shared with creators
						and their managers or agencies so a campaign can run
					</li>
					<li>
						<strong>Authorities, regulators or advisers</strong> where required by law or to protect rights,
						property or safety
					</li>
					<li>
						<strong>A buyer or successor</strong>, in connection with a merger, acquisition or asset sale,
						subject to appropriate safeguards
					</li>
				</Bullets>
			</NoticeSection>

			<NoticeSection title="Where it goes">
				<p>
					We are incorporated in Singapore and use service providers in a range of countries, so your data may
					be transferred to, stored or processed outside your own country. Where we transfer personal data
					across borders we take the steps required by applicable law to ensure a comparable standard of
					protection — under the PDPA, by ensuring the recipient is bound by legally enforceable obligations
					to a comparable standard.
				</p>
			</NoticeSection>

			<NoticeSection title="How long we keep it">
				<Table role="table" aria-label="Retention periods">
					<TableRow $header role="row">
						<span role="columnheader">What</span>
						<span role="columnheader">How long</span>
					</TableRow>
					{RETENTION.map(([what, howLong]) => (
						<TableRow key={what} role="row">
							<span role="cell">{what}</span>
							<span role="cell">{howLong}</span>
						</TableRow>
					))}
				</Table>
				<p>We keep personal data only for as long as we need it, and no longer.</p>
			</NoticeSection>

			<NoticeSection title="Your rights">
				<p>
					Depending on where you are and which law applies, you may have the right to <strong>access</strong>{" "}
					your personal data, have it <strong>corrected</strong>, ask for it to be <strong>deleted</strong>,{" "}
					<strong>object to or restrict</strong> certain processing including marketing, receive a{" "}
					<strong>copy</strong> of data you gave us, and <strong>withdraw consent</strong>.
				</p>
				<p>
					To exercise any of these, email <strong>privacy@thecreatorverse.co</strong>. We will respond within
					the timeframes required by applicable law. You can also complain to your local data protection
					authority; in Singapore that is the Personal Data Protection Commission (PDPC).
				</p>
				<p>
					We may need to keep some records — in particular records of your acceptance of our terms — even
					after a deletion request, where the law requires it.
				</p>
			</NoticeSection>

			<NoticeSection title="Cookies">
				<p>
					We use cookies and similar technologies to operate and secure the Platform, remember your
					preferences and analyse usage. You can manage cookies through your browser settings and, where
					required, our consent banner. Our Cookie Policy has the detail.
				</p>
			</NoticeSection>

			<NoticeSection title="Security and changes">
				<p>
					We use access controls, encryption in transit and monitoring to protect personal data. No system is
					completely secure. Where a notifiable data breach occurs we will notify the relevant authority,
					including the PDPC, and affected individuals, as and where required by law.
				</p>
				<p>
					We may update this Notice. We will post the updated version with a new date and, where changes are
					material, give additional notice as required by law.
				</p>
			</NoticeSection>
		</Content>
	</Document>
);
